import React, { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { useAuth } from "../hooks/useAuth.js";
import { trpc } from "../lib/trpc.js";
import { BrainCircuit, Users, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

export function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const [, navigate] = useLocation();

  // Parse invite code from URL
  const params = new URLSearchParams(window.location.search);
  const inviteCode = params.get("invite") || "";

  // Validate invite code
  const { data: inviteData, isLoading: inviteLoading } = trpc.organization.validateInvite.useQuery(
    { inviteCode },
    { enabled: !!inviteCode }
  );

  const acceptInvite = trpc.organization.acceptInvite.useMutation();

  const isInviteMode = !!inviteCode && inviteData?.valid;

  // Pre-fill email from invite
  useEffect(() => {
    if (inviteData?.valid && inviteData.email) {
      setEmail(inviteData.email);
    }
  }, [inviteData]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (isInviteMode) {
        // Accept invite flow
        const result = await acceptInvite.mutateAsync({
          inviteCode,
          email,
          password,
          name: name || undefined,
        });
        // Store tokens and redirect
        if (result.accessToken) {
          localStorage.setItem("accessToken", result.accessToken);
          localStorage.setItem("refreshToken", result.refreshToken);
          window.location.href = "/";
        }
      } else {
        // Normal registration flow
        await register({ name, email, password, organizationName });
        window.location.href = "/";
      }
    } catch (err: any) {
      setError(err.message || "Falha no registro");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-orange-600 flex items-center justify-center mb-4 shadow-lg shadow-primary/20">
            <BrainCircuit className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">
            <span className="text-primary">Y</span>monetize Brain
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isInviteMode ? "Você foi convidado para uma organização" : "Crie sua conta e comece a monetizar"}
          </p>
        </div>

        {/* Invite Banner */}
        {inviteCode && (
          <div className={`mb-4 p-4 rounded-xl border ${
            inviteLoading ? "bg-secondary/50 border-border" :
            inviteData?.valid ? "bg-success/5 border-success/20" :
            "bg-destructive/5 border-destructive/20"
          }`}>
            {inviteLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" /> Verificando convite...
              </div>
            ) : inviteData?.valid ? (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center shrink-0">
                  <Users className="w-5 h-5 text-success" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Convite para <span className="text-primary">{inviteData.organizationName}</span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Papel: <span className="capitalize">{inviteData.role}</span>
                  </p>
                </div>
                <CheckCircle2 className="w-5 h-5 text-success ml-auto shrink-0" />
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {inviteData?.reason || "Convite inválido"}
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-card border border-border rounded-xl p-6 space-y-4">
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 text-destructive text-sm p-3 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" /> {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Nome</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 bg-background border border-input rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="Seu nome"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 bg-background border border-input rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="voce@exemplo.com"
              required
              disabled={isInviteMode && !!inviteData?.email}
            />
          </div>

          {/* Organization name only for normal registration */}
          {!isInviteMode && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Organização</label>
              <input
                type="text"
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                className="w-full px-3 py-2 bg-background border border-input rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="Nome da sua empresa"
                required
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Senha</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 bg-background border border-input rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="Mínimo 6 caracteres"
              minLength={6}
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading || (!!inviteCode && !inviteData?.valid)}
            className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> {isInviteMode ? "Aceitando convite..." : "Criando conta..."}</>
            ) : (
              isInviteMode ? "Aceitar Convite e Criar Conta" : "Criar conta"
            )}
          </button>
        </form>

        <p className="text-center text-sm text-muted-foreground mt-4">
          Já tem uma conta?{" "}
          <Link href="/" className="text-primary hover:underline">
            Entrar
          </Link>
        </p>
      </div>
    </div>
  );
}
